import { useCallback, useEffect, useRef, useState } from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import type { Dayjs } from "@thalamos/common";
import { dayjs } from "@thalamos/common";
import type { GuestSessionAccreditation } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import {
  Banner,
  BannerList,
  Button,
  DateField,
  RadioButton,
  TextField,
} from "../../../components/design-system/index.js";
import { PopupDialog } from "../../../components/design-system/PopupDialog/PopupDialog.js";
import { PopupDialogTitle } from "../../../components/design-system/PopupDialog/PopupDialogTitle.js";
import { assertOkResponse } from "../../../helpers/assertOkResponse.js";
import { routeFns } from "../../../routePaths.js";

interface GuestAccreditationGateDialogProps {
  onAccreditationSubmitted: (accreditation: GuestSessionAccreditation) => void;
}

type LookupState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "found"; section12ExpiryDate: string }
  | { status: "not-found" }
  | { status: "error" };

const DEBOUNCE_MS = 500;

export function GuestAccreditationGateDialog({
  onAccreditationSubmitted,
}: GuestAccreditationGateDialogProps) {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [gmcNumber, setGmcNumber] = useState("");
  const [lookupState, setLookupState] = useState<LookupState>({
    status: "idle",
  });
  // Whether the current expiry date value was synced from the approvals API
  // (turns false when the user manually edits the date)
  const [syncedWithApi, setSyncedWithApi] = useState(false);
  const [s12Choice, setS12Choice] = useState<"yes" | "no" | undefined>(
    undefined,
  );
  const [expiryDate, setExpiryDate] = useState<Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const lookupRequestRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isGmcNumberValid = /^\d{7}$/.test(gmcNumber.trim());

  const doLookup = useCallback(() => {
    if (!isGmcNumberValid) return;

    lookupRequestRef.current += 1;
    const requestId = lookupRequestRef.current;

    setLookupState({ status: "loading" });

    void api.users
      .lookupGmcNumber(gmcNumber.trim())
      .then(assertOkResponse)
      .then((result) => {
        if (requestId !== lookupRequestRef.current) return;
        if (result.data.found) {
          const apiDate = result.data.section12ExpiryDate!;
          setLookupState({ status: "found", section12ExpiryDate: apiDate });
          // Pre-populate fields from API result
          setS12Choice("yes");
          setExpiryDate(dayjs(apiDate));
          setSyncedWithApi(true);
        } else {
          setLookupState({ status: "not-found" });
          setS12Choice(undefined);
          setExpiryDate(null);
          setSyncedWithApi(false);
        }
      })
      .catch(() => {
        if (requestId !== lookupRequestRef.current) return;
        setLookupState({ status: "error" });
        setS12Choice(undefined);
        setExpiryDate(null);
        setSyncedWithApi(false);
      });
  }, [gmcNumber, isGmcNumberValid]);

  // Debounced auto-lookup when GMC number becomes valid
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!isGmcNumberValid) return;

    debounceTimerRef.current = setTimeout(() => {
      doLookup();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [doLookup, isGmcNumberValid]);

  const isSection12Approved = s12Choice === "yes";

  // Can submit once lookup has completed and user has made a choice
  const canSubmit =
    fullName.trim().length > 0 &&
    isGmcNumberValid &&
    lookupState.status !== "idle" &&
    lookupState.status !== "loading" &&
    s12Choice !== undefined &&
    (s12Choice === "no" || (expiryDate !== null && expiryDate.isValid()));

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await api.users
        .submitGuestSessionAccreditation({
          fullName: fullName.trim(),
          gmcNumber: gmcNumber.trim(),
          isSection12Approved,
          overrideApproval: !syncedWithApi ? true : undefined,
          section12ExpiryDate:
            isSection12Approved && expiryDate?.isValid()
              ? expiryDate.format("YYYY-MM-DD")
              : undefined,
        })
        .then(assertOkResponse);
      onAccreditationSubmitted(result.data);
    } catch {
      setSubmitError("Failed to save your accreditation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    fullName,
    gmcNumber,
    isSection12Approved,
    syncedWithApi,
    expiryDate,
    onAccreditationSubmitted,
  ]);

  const showS12Section =
    lookupState.status !== "idle" &&
    (lookupState.status !== "loading" ||
      s12Choice !== undefined ||
      expiryDate !== null);

  const restoreApiApproval = useCallback(() => {
    if (lookupState.status !== "found") {
      return;
    }

    setExpiryDate(dayjs(lookupState.section12ExpiryDate));
    setSyncedWithApi(true);
  }, [lookupState]);

  // Only show validation after the user has typed at least 7 characters
  const showGmcError = gmcNumber.trim().length >= 7 && !isGmcNumberValid;

  const gmcEndAdornment =
    lookupState.status === "loading" ? (
      <InputAdornment position="end">
        <CircularProgress size={20} sx={{ color: "#8ABE23" }} />
      </InputAdornment>
    ) : undefined;

  return (
    <PopupDialog
      open={true}
      maxWidth="sm"
      testId="guest-accreditation-gate-dialog"
    >
      <PopupDialogTitle
        titleText="Confirm your details"
        subtitleText="Before you can work on this admission, please confirm your name and GMC number so we can verify your Section 12 approval status."
        closeDialog={() => {
          navigate(routeFns.home());
        }}
      />

      <DialogContent sx={{ pt: 0 }}>
        <TextField
          name="fullName"
          label="Full name"
          value={fullName}
          onChange={(value) => setFullName(value)}
          placeholder="e.g. Dr Jane Smith"
          useFullWidth
        />

        <Box sx={{ mt: 2 }}>
          <TextField
            name="gmcNumber"
            label="GMC number"
            value={gmcNumber}
            onChange={(value) => setGmcNumber(value)}
            placeholder="e.g. 1234567"
            useFullWidth
            InputProps={{ endAdornment: gmcEndAdornment }}
            errorMessage={
              showGmcError ? "GMC number must be exactly 7 digits" : undefined
            }
            showHelperText={showGmcError}
          />
        </Box>

        {/* Warning banner when not found or API error */}
        {lookupState.status === "not-found" && (
          <Box sx={{ mt: 1 }}>
            <Banner
              bannerType={BannerList.WARNING}
              title="This GMC number was not found on the Section 12 approvals register."
            />
          </Box>
        )}
        {lookupState.status === "error" && (
          <Box sx={{ mt: 1 }}>
            <Banner
              bannerType={BannerList.WARNING}
              title="We could not check the Section 12 approvals register."
            />
          </Box>
        )}

        {/* S12 fields — shown once lookup completes */}
        {showS12Section && (
          <Box sx={{ mt: 2 }}>
            <RadioButton
              name="s12ApprovalChoice"
              label="Are you Section 12 approved?"
              options={[
                { label: "Yes", value: "yes" },
                { label: "No", value: "no" },
              ]}
              selected={s12Choice}
              onChange={(value: string) => {
                const choice = value as "yes" | "no";
                setS12Choice(choice);
                if (choice === "no") {
                  setExpiryDate(null);
                  setSyncedWithApi(false);
                  return;
                }

                restoreApiApproval();
              }}
            />

            {s12Choice === "yes" && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ flex: 1 }}>
                  <DateField
                    name="section12ExpiryDate"
                    label="Section 12 expiry date"
                    value={expiryDate}
                    onChange={(value) => {
                      setExpiryDate(value);
                      // User is manually editing — no longer synced with API
                      setSyncedWithApi(false);
                    }}
                    minDate={dayjs()}
                  />
                </Box>
                {(lookupState.status === "loading" ||
                  expiryDate?.isValid()) && (
                  <Tooltip
                    title={
                      lookupState.status === "loading"
                        ? "Checking Section 12 register..."
                        : syncedWithApi
                          ? "Verified against Section 12 register. Click to re-verify."
                          : "Not verified against Section 12 register. Click to verify."
                    }
                  >
                    <IconButton
                      onClick={doLookup}
                      size="small"
                      sx={{ p: 0.25, ml: 0.5 }}
                      aria-label={
                        lookupState.status === "loading"
                          ? "Checking Section 12 approval"
                          : syncedWithApi
                            ? "Re-verify Section 12 approval"
                            : "Verify Section 12 approval"
                      }
                      disabled={lookupState.status === "loading"}
                    >
                      {lookupState.status === "loading" ? (
                        <CircularProgress size={20} sx={{ color: "#8ABE23" }} />
                      ) : syncedWithApi ? (
                        <VerifiedUserIcon
                          sx={{ color: "#8ABE23", fontSize: 22 }}
                        />
                      ) : (
                        <ErrorOutlineIcon
                          sx={{ color: "#C74418", fontSize: 22 }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        )}

        {submitError && (
          <Box sx={{ mt: 2 }}>
            <Banner bannerType={BannerList.ERROR} title={submitError} />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ display: "flex", justifyContent: "flex-end", px: 3, pb: 2 }}
      >
        <Button
          label="Confirm and continue"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          testId="submit-accreditation-button"
        />
      </DialogActions>
    </PopupDialog>
  );
}
